import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import './Admin.css';

interface Work {
  id: number;
  workshop_type: string;
  title: string;
  content: string;
  class_name: string;
  nickname: string;
  status: 'pending' | 'approved' | 'rejected';
  is_displayed: boolean;
  created_at: string;
  email?: string;
  username?: string;
}

const Admin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'pending' | 'all'>('pending');
  const [works, setWorks] = useState<Work[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [workshopTypeFilter, setWorkshopTypeFilter] = useState<string>('');

  useEffect(() => {
    loadWorks();
  }, [activeTab, page, statusFilter, workshopTypeFilter]);

  const loadWorks = async () => {
    setLoading(true);
    try {
      if (activeTab === 'pending') {
        const response = await api.get(`/works/admin/pending?page=${page}&limit=20`);
        setWorks(response.data.works);
        setTotal(response.data.total);
      } else {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: '20'
        });
        if (statusFilter) params.append('status', statusFilter);
        if (workshopTypeFilter) params.append('workshopType', workshopTypeFilter);
        
        const response = await api.get(`/works/admin/all?${params.toString()}`);
        setWorks(response.data.works);
        setTotal(response.data.total);
      }
    } catch (error: any) {
      alert(error.response?.data?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (workId: number, status: 'approved' | 'rejected') => {
    if (!window.confirm(`确定要${status === 'approved' ? '通过' : '拒绝'}这个作品吗？`)) {
      return;
    }

    try {
      await api.put(`/works/admin/${workId}/review`, { status });
      alert('审核成功');
      loadWorks();
    } catch (error: any) {
      alert(error.response?.data?.message || '操作失败');
    }
  };

  const handleToggleDisplay = async (workId: number, currentDisplay: boolean) => {
    try {
      await api.put(`/works/admin/${workId}/display`, { is_displayed: !currentDisplay });
      alert(currentDisplay ? '作品已关闭展示' : '作品已开启展示');
      loadWorks();
    } catch (error: any) {
      alert(error.response?.data?.message || '操作失败');
    }
  };

  const getWorkshopTypeName = (type: string) => {
    const names: { [key: string]: string } = {
      audio: '声工坊',
      video: '影工坊',
      image: '图工坊',
      text: '文工坊'
    };
    return names[type] || type;
  };

  const getStatusBadge = (status: string) => {
    const badges: { [key: string]: { text: string; className: string } } = {
      pending: { text: '待审核', className: 'status-pending' },
      approved: { text: '已通过', className: 'status-approved' },
      rejected: { text: '已拒绝', className: 'status-rejected' }
    };
    return badges[status] || { text: status, className: '' };
  };

  return (
    <div className="admin-container">
      <h1 className="page-title">作品审核管理</h1>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('pending');
            setPage(1);
            setStatusFilter('');
            setWorkshopTypeFilter('');
          }}
        >
          待审核 ({activeTab === 'pending' ? total : ''})
        </button>
        <button
          className={`admin-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('all');
            setPage(1);
          }}
        >
          全部作品
        </button>
      </div>

      {activeTab === 'all' && (
        <div className="admin-filters">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="filter-select"
          >
            <option value="">全部状态</option>
            <option value="pending">待审核</option>
            <option value="approved">已通过</option>
            <option value="rejected">已拒绝</option>
          </select>
          <select
            value={workshopTypeFilter}
            onChange={(e) => {
              setWorkshopTypeFilter(e.target.value);
              setPage(1);
            }}
            className="filter-select"
          >
            <option value="">全部工坊</option>
            <option value="audio">声工坊</option>
            <option value="video">影工坊</option>
            <option value="image">图工坊</option>
            <option value="text">文工坊</option>
          </select>
        </div>
      )}

      {loading ? (
        <div className="loading">加载中...</div>
      ) : works.length === 0 ? (
        <div className="empty-state">暂无作品</div>
      ) : (
        <>
          <div className="works-list">
            {works.map((work) => {
              const statusBadge = getStatusBadge(work.status);
              return (
                <div key={work.id} className="work-item">
                  <div className="work-header">
                    <div className="work-title-row">
                      <h3 className="work-title">{work.title}</h3>
                      <span className={`status-badge ${statusBadge.className}`}>
                        {statusBadge.text}
                      </span>
                    </div>
                    <div className="work-meta">
                      <span className="work-type">{getWorkshopTypeName(work.workshop_type)}</span>
                      <span className="work-author">
                        {work.class_name} · {work.nickname}
                      </span>
                      {work.email && (
                        <span className="work-email">({work.email})</span>
                      )}
                      <span className="work-date">
                        {new Date(work.created_at).toLocaleString('zh-CN')}
                      </span>
                    </div>
                  </div>

                  <div className="work-content">
                    {work.workshop_type === 'image' && work.content.startsWith('/uploads/') ? (
                      <img src={work.content} alt={work.title} className="work-image" />
                    ) : work.workshop_type === 'audio' && work.content.startsWith('/uploads/') ? (
                      <audio src={work.content} controls className="work-audio" />
                    ) : work.workshop_type === 'video' ? (
                      <a href={work.content} target="_blank" rel="noopener noreferrer" className="work-video-link">
                        {work.content}
                      </a>
                    ) : (
                      <p className="work-text">{work.content}</p>
                    )}
                  </div>

                  <div className="work-actions">
                    {work.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleReview(work.id, 'approved')}
                          className="btn btn-approve"
                        >
                          通过
                        </button>
                        <button
                          onClick={() => handleReview(work.id, 'rejected')}
                          className="btn btn-reject"
                        >
                          拒绝
                        </button>
                      </>
                    )}
                    {work.status === 'approved' && (
                      <button
                        onClick={() => handleToggleDisplay(work.id, work.is_displayed)}
                        className={`btn ${work.is_displayed ? 'btn-hide' : 'btn-show'}`}
                      >
                        {work.is_displayed ? '关闭展示' : '开启展示'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pagination">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn btn-secondary"
            >
              上一页
            </button>
            <span className="page-info">
              第 {page} 页 / 共 {Math.ceil(total / 20)} 页
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= Math.ceil(total / 20)}
              className="btn btn-secondary"
            >
              下一页
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Admin;

